import type { ReactElement } from 'react';

import { createBootstrapInfo } from './bootstrap';
import { GameLab } from './game/GameLab';
import { COLD_START_HINT_MS, type ProbePhase, type ProbeState } from './healthProbe';
import { useHealthProbe } from './useHealthProbe';

const PHASE_LABEL: Record<ProbePhase, string> = {
  idle: 'Idle',
  probing: 'Checking…',
  waking: 'Waking server…',
  healthy: 'Healthy',
  unreachable: 'Unreachable',
};

function formatSeconds(ms: number): string {
  return `${(ms / 1000).toFixed(1)}s`;
}

/**
 * Explica que esta pasando en cada fase.
 *
 * El caso importante es `waking`: en Render Free el servicio se apaga tras 15
 * minutos sin trafico y tarda cerca de un minuto en volver. Sin este mensaje,
 * un arranque en frio es indistinguible de una caida.
 */
function describePhase(state: ProbeState): string {
  switch (state.phase) {
    case 'idle':
      return 'Preparando la comprobacion.';
    case 'probing':
      return 'Contactando con el servidor.';
    case 'waking':
      return `El servidor tarda mas de ${formatSeconds(COLD_START_HINT_MS)}. En el plan gratuito se apaga tras 15 minutos sin trafico y necesita cerca de un minuto para arrancar. Reintentando.`;
    case 'healthy':
      return `Respondio correctamente tras ${String(state.attempt)} intento(s) en ${formatSeconds(state.elapsedMs)}.`;
    case 'unreachable':
      return 'No respondio dentro del presupuesto de espera.';
  }
}

function HealthDashboard({ httpBaseUrl }: { httpBaseUrl: string }): ReactElement {
  const { state, retry } = useHealthProbe(httpBaseUrl);
  const busy = state.phase === 'probing' || state.phase === 'waking';

  return (
    <section className="panel" aria-live="polite">
      <header className="panel-head">
        <h2 className="panel-title">Server health</h2>
        <span className={`badge badge-${state.phase}`} data-testid="health-phase">
          {PHASE_LABEL[state.phase]}
        </span>
      </header>

      <p className="panel-note">{describePhase(state)}</p>

      <dl className="facts">
        <dt>Endpoint</dt>
        <dd data-testid="health-endpoint">{httpBaseUrl}/health</dd>

        <dt>Attempts</dt>
        <dd>{state.attempt}</dd>

        <dt>Elapsed</dt>
        <dd>{formatSeconds(state.elapsedMs)}</dd>

        {state.payload ? (
          <>
            <dt>Service</dt>
            <dd>{state.payload.service}</dd>

            <dt>Server version</dt>
            <dd>{state.payload.version}</dd>
          </>
        ) : null}

        {state.error !== null && state.phase !== 'healthy' ? (
          <>
            <dt>Last error</dt>
            <dd className="error">{state.error}</dd>
          </>
        ) : null}
      </dl>

      <button type="button" className="action" onClick={retry} disabled={busy}>
        {busy ? 'Checking…' : 'Check again'}
      </button>
    </section>
  );
}

/** Pantalla principal: identidad de la app y estado real del servidor. */
export function App(): ReactElement {
  const info = createBootstrapInfo();

  return (
    <main className="shell">
      <h1 className="title">{info.appName}</h1>
      <p className="tagline">{info.tagline}</p>

      <dl className="facts">
        <dt>Version</dt>
        <dd data-testid="version">{info.version}</dd>

        <dt>Game server</dt>
        <dd data-testid="server-url">{info.serverUrl}</dd>

        <dt>Game connection</dt>
        <dd data-testid="status">{info.connectionStatus}</dd>
      </dl>

      <HealthDashboard httpBaseUrl={info.serverHttpUrl} />
      <GameLab />
    </main>
  );
}
